/**
 * Token management utilities for Report Summarizer
 * Handles JWT tokens and cookie storage following HappyCo patterns
 */

import Cookies from 'js-cookie'
import { TokenInfo } from './types'

const ACCESS_TOKEN_COOKIE = 'access_token'
const WEB_SESSION_TOKEN_COOKIE = 'web_session_token'
const TOKEN_EXPIRES_COOKIE = 'token_expires_at'

/**
 * Cookie options following HappyCo security standards
 */
const cookieOptions: Cookies.CookieAttributes = {
  secure: window.location.protocol === 'https:',
  sameSite: 'lax',
  path: '/',
}

/**
 * Stores authentication tokens in cookies
 */
export function storeTokens(accessToken: string, webSessionToken?: string): void {
  try {
    // Parse JWT to get expiration time
    const payload = JSON.parse(atob(accessToken.split('.')[1]))
    const expiresAt = payload.exp * 1000 // Convert to milliseconds
    
    Cookies.set(ACCESS_TOKEN_COOKIE, accessToken, {
      ...cookieOptions,
      expires: new Date(expiresAt),
    })
    
    if (webSessionToken) {
      Cookies.set(WEB_SESSION_TOKEN_COOKIE, webSessionToken, {
        ...cookieOptions,
        expires: new Date(expiresAt),
      })
    }
    
    Cookies.set(TOKEN_EXPIRES_COOKIE, expiresAt.toString(), {
      ...cookieOptions,
      expires: new Date(expiresAt),
    })
  } catch (error) {
    console.error('Failed to store tokens:', error)
  }
}

/**
 * Retrieves the access token from cookies
 */
export function getAccessToken(): string | null {
  return Cookies.get(ACCESS_TOKEN_COOKIE) || null
}

/**
 * Retrieves the web session token from cookies
 */
export function getWebSessionToken(): string | null {
  return Cookies.get(WEB_SESSION_TOKEN_COOKIE) || null
}

/**
 * Retrieves all token information
 */
export function getTokenInfo(): TokenInfo {
  const accessToken = getAccessToken()
  const webSessionToken = getWebSessionToken()
  const expiresAtStr = Cookies.get(TOKEN_EXPIRES_COOKIE)
  const expiresAt = expiresAtStr ? parseInt(expiresAtStr, 10) : null
  
  return {
    accessToken,
    webSessionToken,
    expiresAt,
  }
}

/**
 * Checks if the current token is expired or will expire soon
 */
export function isTokenExpired(): boolean {
  const { expiresAt } = getTokenInfo()
  if (!expiresAt) return true
  
  // Consider token expired if it expires within the next 5 minutes
  const fiveMinutesFromNow = Date.now() + (5 * 60 * 1000)
  return expiresAt <= fiveMinutesFromNow
}

/**
 * Clears all authentication tokens
 */
export function clearTokens(): void {
  Cookies.remove(ACCESS_TOKEN_COOKIE, cookieOptions)
  Cookies.remove(WEB_SESSION_TOKEN_COOKIE, cookieOptions)
  Cookies.remove(TOKEN_EXPIRES_COOKIE, cookieOptions)
}

/**
 * Extracts user information from JWT token
 */
export function getUserFromToken(token: string): any {
  try {
    const payload = JSON.parse(atob(token.split('.')[1]))
    return {
      id: payload.sub,
      email: payload.email,
      firstName: payload.given_name,
      lastName: payload.family_name,
      name: payload.name || `${payload.given_name || ''} ${payload.family_name || ''}`.trim(),
      sub: payload.sub,
      preferred_username: payload.preferred_username,
      given_name: payload.given_name,
      family_name: payload.family_name,
    }
  } catch (error) {
    console.error('Failed to parse user from token:', error)
    return null
  }
}